import { useState } from 'react';
import { Loader2, AlertCircle, MessageSquare, Send, ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../../services/supportService';
import { TICKET_STATUS_BADGE } from '../../types/support';
import type { SupportTicket, TicketStatus } from '../../types/support';

const STATUS_TABS: { label: string; value: TicketStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Closed', value: 'closed' },
];

const STATUS_ACTIONS: Record<string, { label: string; next: TicketStatus }[]> = {
  open: [{ label: 'Start Progress', next: 'in_progress' }, { label: 'Close', next: 'closed' }],
  in_progress: [{ label: 'Mark Resolved', next: 'resolved' }, { label: 'Close', next: 'closed' }],
  resolved: [{ label: 'Reopen', next: 'open' }, { label: 'Close', next: 'closed' }],
  closed: [],
};

function TicketRow({ ticket }: { ticket: SupportTicket }) {
  const [expanded, setExpanded] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const queryClient = useQueryClient();

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-ticket-detail', ticket.id],
    queryFn: () => supportService.adminGetTicket(ticket.id),
    enabled: expanded,
  });

  const replyMutation = useMutation({
    mutationFn: () => supportService.adminReply(ticket.id, replyBody.trim()),
    onSuccess: () => {
      setReplyBody('');
      queryClient.invalidateQueries({ queryKey: ['admin-ticket-detail', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: TicketStatus) => supportService.changeTicketStatus(ticket.id, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ticket-detail', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
    },
  });

  const actions = STATUS_ACTIONS[ticket.status] ?? [];

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      {/* Row header */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-750 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{ticket.subject}</p>
          <p className="text-slate-500 text-xs mt-0.5">
            #{ticket.id}
            {ticket.user_email && <span className="text-slate-400"> · {ticket.user_email}</span>}
            {' · '}Updated {new Date(ticket.updated_at).toLocaleString()}
          </p>
        </div>
        <span
          className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${TICKET_STATUS_BADGE[ticket.status] ?? TICKET_STATUS_BADGE.open}`}
        >
          {ticket.status.replace('_', ' ')}
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-700 p-4 space-y-4">
          {detailLoading && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 text-teal-400 animate-spin" />
            </div>
          )}

          {detail && (
            <>
              {/* Message thread */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {detail.messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                        msg.is_admin ? 'bg-indigo-900/40 text-indigo-100' : 'bg-slate-700 text-slate-200'
                      }`}
                    >
                      {msg.is_admin && (
                        <p className="text-xs text-indigo-400 mb-1 font-medium">Admin</p>
                      )}
                      <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                      <p className={`text-xs mt-1 ${msg.is_admin ? 'text-indigo-400' : 'text-slate-500'}`}>
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Admin reply */}
              {ticket.status !== 'closed' && (
                <div className="space-y-2">
                  <textarea
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                    rows={2}
                    placeholder="Reply as admin..."
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                  />
                  {replyMutation.isError && (
                    <p className="text-red-400 text-xs flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Failed to send reply
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {actions.map((action) => (
                        <button
                          key={action.next}
                          onClick={() => statusMutation.mutate(action.next)}
                          disabled={statusMutation.isPending}
                          className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => replyMutation.mutate()}
                      disabled={!replyBody.trim() || replyMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs transition-colors"
                    >
                      {replyMutation.isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Send className="w-3 h-3" />
                      )}
                      Send Reply
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupportAdmin() {
  const [activeTab, setActiveTab] = useState<TicketStatus | 'all'>('all');

  const { data: tickets, isLoading, error } = useQuery({
    queryKey: ['admin-tickets', activeTab],
    queryFn: () => supportService.adminListTickets(activeTab === 'all' ? undefined : activeTab),
  });

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="w-6 h-6 text-indigo-400" />
        <h1 className="text-xl font-semibold text-white">Support Tickets</h1>
        {tickets && (
          <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded-full">
            {tickets.length}
          </span>
        )}
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 mb-4 bg-slate-800 p-1 rounded-lg border border-slate-700 w-fit">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              activeTab === tab.value
                ? 'bg-indigo-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-4 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          Failed to load tickets
        </div>
      )}

      {tickets && tickets.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No tickets in this category.</p>
        </div>
      )}

      {tickets && tickets.length > 0 && (
        <div className="space-y-2">
          {tickets.map((ticket) => (
            <TicketRow key={ticket.id} ticket={ticket} />
          ))}
        </div>
      )}
    </div>
  );
}
